import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export default function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, position: 'top' });
  const iconRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!iconRef.current) return;
    const rect = iconRef.current.getBoundingClientRect();
    const tooltipWidth = 224; // w-56 = 14rem = 224px
    const tooltipHeight = 80; // approximate max height
    const margin = 8;

    // Default: show above
    let position = 'top';
    let top = rect.top - tooltipHeight - margin;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;

    // If it would go above viewport, show below
    if (top < 4) {
      position = 'bottom';
      top = rect.bottom + margin;
    }

    // Keep within horizontal bounds
    if (left < 4) left = 4;
    if (left + tooltipWidth > window.innerWidth - 4) {
      left = window.innerWidth - tooltipWidth - 4;
    }

    setCoords({ top, left, position });
  }, []);

  useEffect(() => {
    if (visible) {
      updatePosition();
    }
  }, [visible, updatePosition]);

  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        ref={iconRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300 text-[10px] font-bold leading-none hover:bg-gray-400 dark:hover:bg-gray-500 cursor-help transition-colors"
        aria-label="More information"
        tabIndex={0}
      >
        i
      </button>
      {visible && createPortal(
        <span
          role="tooltip"
          className="fixed z-[9999] w-56 px-3 py-2 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded-lg shadow-lg leading-relaxed pointer-events-none"
          style={{ top: coords.top, left: coords.left }}
        >
          {text}
          <span
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45 ${
              coords.position === 'top' ? '-bottom-1' : '-top-1'
            }`}
          />
        </span>,
        document.body
      )}
    </span>
  );
}
